import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  Row,
} from 'reactstrap';
import Select from 'react-select';
import { Controller } from 'react-hook-form';
import ReactBSAlert from 'react-bootstrap-sweetalert';
import { useNavigate } from 'react-router-dom';

import TimelineHeader from '../../components/headers/TimelineHeader';
import ProfileCard from '../../components/ProfileCard';
import CalendarViewControls from '../../components/CalendarViewControls';
import { useVisitCalendar } from '../../hooks/useVisitCalendar';
import { useManagerUnitPcvCalendar } from './hooks/useManagerUnitPcvCalendar';
import {
  getPcvVisitStatusLabel,
  getPcvVisitBadgeColor,
} from './adapters/unitPcvVisitAdapter';
import { VisitPreSaleState } from '../../constants/visitsConstants';
import { formatStandarCalendarDate } from '../../adapters/dateAdapter';

const UnitPcvVisitsCalendar = () => {
  const navigate = useNavigate();
  const {
    landlordEmail,
    setLandlordEmail,
    landlordFound,
    handleSearchLandlord,
    formError,
    modalAdd,
    isEdit,
    modalView,
    setModalView,
    modalDoneCancelled,
    setModalDoneCancelled,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    closeAddEditModal,
    startDate,
    creationStartDate,
    hours,
    fetchEvents,
    onSelect,
    onEventClick,
    onEventChange,
    event,
    control,
    setValue,
    errors,
    doCreateVisit,
    doRescheduleVisit,
    handleDeleteVisit,
    handleAcceptVisit,
    handleCompleteVisit,
    propersOptions,
    selectedProperId,
    setSelectedProperId,
    pcvInfo,
  } = useManagerUnitPcvCalendar();

  const { calendarRef, changeCalendarView, addCalendarEvent } =
    useVisitCalendar({
      fetchEvents,
      onSelect,
      onEventClick,
      onEventChange,
    });

  return (
    <>
      <TimelineHeader name="Administrar Asesorías PCV" parentName="PCVs" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-center py-4">
                  <Col lg="6">
                    <h6 className="fullcalendar-title h2 text-white d-inline-block mb-0 mr-1">
                      {event?.currentDate}
                    </h6>
                    {pcvInfo && (
                      <p className="text-muted mb-0">
                        {pcvInfo.project_name} [#{pcvInfo.code} —
                        {pcvInfo.model_name} — {pcvInfo.name}]
                      </p>
                    )}
                  </Col>
                  <Col className="mt-3 mt-lg-0 text-lg-right" lg="6">
                    <CalendarViewControls onChangeView={changeCalendarView} />
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <div
                  className="calendar"
                  data-toggle="calendar"
                  id="calendar"
                  ref={calendarRef}
                />
              </CardBody>
              <CardFooter />
            </Card>
          </div>
        </Row>
      </Container>

      {/* Modal add / reschedule */}
      <Modal
        isOpen={modalAdd}
        toggle={closeAddEditModal}
        className="modal-dialog-centered modal-secondary"
      >
        <div className="modal-header">
          <Row>
            <Col md="12">
              <Alert color="default">
                {isEdit
                  ? `Reagendar asesoría al ${formatStandarCalendarDate(event?.start, false)}`
                  : `Agendar asesoría el ${formatStandarCalendarDate(creationStartDate || '', false)}`}
              </Alert>
            </Col>
          </Row>
        </div>
        <form
          onSubmit={
            isEdit ? doRescheduleVisit : doCreateVisit(addCalendarEvent)
          }
        >
          <div className="modal-body">
            {!isEdit && (
              <FormGroup>
                <label className="form-control-label">
                  Asignar Inversionista
                </label>
                <InputGroup className="mb-3">
                  <Input
                    onChange={({ target }) =>
                      setLandlordEmail(target.value || '')
                    }
                    placeholder="Correo de inversionista"
                    type="text"
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      onClick={handleSearchLandlord}
                      color="primary"
                      outline
                    >
                      Buscar
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>
            )}
            {landlordFound && (
              <Row>
                <Col md="12">
                  <h5>
                    {isEdit
                      ? 'Inversionista asignado'
                      : 'Inversionista encontrado:'}
                  </h5>
                  <ProfileCard
                    avatar={landlordFound.avatar}
                    name={landlordFound?.name}
                    phone={landlordFound?.phone}
                    showAvatar={true}
                  />
                </Col>
              </Row>
            )}
            {formError && (
              <Row>
                <Col md="12">
                  <Alert color="danger">{formError}</Alert>
                </Col>
              </Row>
            )}
            <FormGroup>
              <label className="form-control-label">Selecciona una hora</label>
              <Controller
                name="start_time"
                control={control}
                render={({ field: { ref } }) => (
                  <Select
                    placeholder="Selecciona una hora"
                    options={hours}
                    isOptionDisabled={(option: any) => option.disabled}
                    onChange={(selected: any) =>
                      setValue('start_time', selected.value)
                    }
                    ref={ref}
                  />
                )}
              />
              {errors.start_time?.message && (
                <Alert color="danger">{errors.start_time.message}</Alert>
              )}
            </FormGroup>
            {!isEdit && (
              <FormGroup>
                <h6 className="form-control-label">Seleccionar proper</h6>
                <Controller
                  name="proper_id"
                  control={control}
                  render={({ field: { ref } }) => (
                    <Select
                      placeholder="Selecciona un proper"
                      options={propersOptions}
                      onChange={(selected: any) =>
                        setValue('proper_id', selected.value)
                      }
                      ref={ref}
                    />
                  )}
                />
                {errors.proper_id?.message && (
                  <Alert color="danger">{errors.proper_id.message}</Alert>
                )}
              </FormGroup>
            )}
            <Row>
              <Col lg="12" className="pb-2">
                <Button
                  block
                  onClick={closeAddEditModal}
                  color="secondary"
                  size="sm"
                >
                  Cerrar
                </Button>
              </Col>
              <Col lg="12" className="pb-2">
                <Button
                  block
                  size="sm"
                  className="new-event--add"
                  color={isEdit ? 'default' : 'primary'}
                  type="submit"
                >
                  {isEdit ? 'Reagendar asesoría' : 'Crear asesoría'}
                </Button>
              </Col>
            </Row>
          </div>
        </form>
      </Modal>

      {/* Modal view active visit */}
      <Modal
        isOpen={modalView}
        toggle={() => setModalView(false)}
        className="modal-dialog-centered modal-secondary"
      >
        <div className="modal-body">
          <Form className="edit-event--form">
            <FormGroup>
              <Badge
                className="badge-lg"
                color={getPcvVisitBadgeColor(event?.extendedProps.status)}
              >
                {getPcvVisitStatusLabel(event?.extendedProps.status)}
              </Badge>
              <Alert className="mt-2" color="default">
                {`Visita: ${formatStandarCalendarDate(event?.start)}${
                  event?.extendedProps.proper_id
                    ? ` — Proper: ${propersOptions.find((p) => p.value === event.extendedProps.proper_id)?.label ?? ''}`
                    : ''
                }`}
              </Alert>
              <label className="form-control-label">Inversionista:</label>
              <ProfileCard
                avatar={event?.extendedProps.landlordInfo?.avatar || ''}
                name={event?.extendedProps.landlordInfo?.name || ''}
                phone={event?.extendedProps.landlordInfo?.phone || ''}
                extraInfo={`${event?.title}`}
                showAvatar={true}
              />
            </FormGroup>
            {event?.extendedProps.status ===
              VisitPreSaleState.PENDING_APPROVAL &&
              !event?.extendedProps.proper_id && (
                <FormGroup>
                  <label className="form-control-label">Asignar proper</label>
                  <Select
                    placeholder="Selecciona un proper"
                    options={propersOptions}
                    onChange={(selected: any) =>
                      setSelectedProperId(selected.value)
                    }
                  />
                </FormGroup>
              )}
            <Row>
              <Col lg="12" className="pb-2">
                <Button
                  block
                  onClick={() => setModalView(false)}
                  color="secondary"
                  size="sm"
                >
                  Cerrar
                </Button>
              </Col>
              <Col lg="12" className="pb-2">
                <Button
                  block
                  onClick={() => setDeleteConfirmOpen(true)}
                  color="danger"
                  size="sm"
                >
                  Eliminar asesoría
                </Button>
              </Col>
              {event?.extendedProps.landlordInfo?.id && (
                <Col lg="12" className="pb-2">
                  <Button
                    block
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/landlord/${event.extendedProps.landlordInfo.id}/profile`,
                      )
                    }
                    color="default"
                  >
                    Ver perfil de inversionista
                  </Button>
                </Col>
              )}
              {event?.extendedProps.status ===
                VisitPreSaleState.PENDING_APPROVAL && (
                <Col lg="12" className="pb-2">
                  <Button
                    block
                    color="warning"
                    size="sm"
                    onClick={handleAcceptVisit}
                    disabled={
                      !event?.extendedProps.proper_id && !selectedProperId
                    }
                  >
                    Aceptar asesoría
                  </Button>
                </Col>
              )}
              {[
                VisitPreSaleState.SCHEDULED,
                VisitPreSaleState.RESCHEDULED,
              ].includes(event?.extendedProps.status) && (
                <Col lg="12" className="pb-2">
                  <Button
                    block
                    onClick={handleCompleteVisit}
                    color="success"
                    size="sm"
                  >
                    Completar asesoría
                  </Button>
                </Col>
              )}
            </Row>
            <input className="edit-event--id" type="hidden" />
          </Form>
        </div>
      </Modal>

      {/* Modal done / cancelled (read-only) */}
      <Modal
        isOpen={modalDoneCancelled}
        toggle={() => setModalDoneCancelled(false)}
        className="modal-dialog-centered modal-secondary"
      >
        <div className="modal-body">
          <Form className="edit-event--form">
            <FormGroup>
              <Badge
                className="badge-lg"
                color={getPcvVisitBadgeColor(event?.extendedProps.status)}
              >
                {getPcvVisitStatusLabel(event?.extendedProps.status)}
              </Badge>
              <Alert className="mt-2" color="default">
                {`Fecha de visita: ${formatStandarCalendarDate(event?.start)}`}
              </Alert>
              <label className="form-control-label">Inversionista:</label>
              <ProfileCard
                avatar={event?.extendedProps.landlordInfo?.avatar || ''}
                name={event?.extendedProps.landlordInfo?.name || ''}
                phone={event?.extendedProps.landlordInfo?.phone || ''}
                extraInfo={`${event?.title}`}
                showAvatar={true}
              />
            </FormGroup>
            <Row>
              <Col lg="12" className="pb-2">
                <Button
                  block
                  size="sm"
                  onClick={() => setModalDoneCancelled(false)}
                  color="secondary"
                >
                  Cerrar
                </Button>
              </Col>
              {event?.extendedProps.landlordInfo?.id && (
                <Col lg="12" className="pb-2">
                  <Button
                    block
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/landlord/${event.extendedProps.landlordInfo.id}/profile`,
                      )
                    }
                    color="default"
                  >
                    Ver perfil de inversionista
                  </Button>
                </Col>
              )}
            </Row>
            <input className="edit-event--id" type="hidden" />
          </Form>
        </div>
      </Modal>

      {/* Delete confirmation alert */}
      {deleteConfirmOpen && (
        <ReactBSAlert
          warning
          title="¿Eliminar asesoría? Esta acción no se puede deshacer."
          onConfirm={handleDeleteVisit}
          onCancel={() => setDeleteConfirmOpen(false)}
          showCancel
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
          confirmBtnText="Sí, eliminar"
          cancelBtnText="Cancelar"
        />
      )}
    </>
  );
};

export default UnitPcvVisitsCalendar;
